/**
 * ServerChildDetails.jsx
 * Renders the expandable child-tab panel for a single server row.
 * Tabs: Asset Info | Storage & Backup | Network & IP | Location |
 *       Services | Applications | Security | Operations | Web Server | Database
 *
 * Each editable field fires an onChange that flows up to the parent
 * (InfrastructureDashboard) so the offline-sync hook can handle it.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiRefreshCw, FiAlertCircle, FiSearch, FiDownload, FiChevronLeft, FiChevronRight, FiEye, FiEyeOff } from 'react-icons/fi';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { fetchChildDetails } from './serverApi';

// ─── Small helpers ────────────────────────────────────────────────────────────

const TABS = [
  { key: 'asset',      label: 'Asset Info' },
  { key: 'backup',     label: 'Storage & Backup' },
  { key: 'network',    label: 'Network & IP' },
  { key: 'location',   label: 'Location Details' },
  { key: 'services',   label: 'Services' },
  { key: 'apps',       label: 'Applications' },
  { key: 'security',   label: 'Security' },
  { key: 'operations', label: 'Operations' },
  { key: 'web',        label: 'Web Server' },
  { key: 'db',         label: 'Database' },
];

function EditInput({ type = 'text', value = '', onChange, className = '', ...rest }) {
  const [local, setLocal] = useState(value ?? '');
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => { setLocal(value ?? ''); }, [value]);

  const actualType = type === 'password' && showPassword ? 'text' : type;

  if (type === 'password') {
    return (
      <div className="relative flex items-center">
        <input
          type={actualType}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => { if (local !== (value ?? '')) onChange(local); }}
          className={`w-full border border-gray-200 rounded px-2 py-1 pr-7 text-xs focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-white transition shadow-sm ${className}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          {showPassword ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
        </button>
      </div>
    );
  }

  return (
    <input
      type={type}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value ?? '')) onChange(local); }}
      className={`w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-white transition shadow-sm ${className}`}
      {...rest}
    />
  );
}

function EditSelect({ value = '', options = [], onChange, className = '' }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);

  return (
    <select
      value={local}
      onChange={(e) => {
        setLocal(e.target.value);
        onChange(e.target.value);
      }}
      className={`w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-red-500 bg-white transition shadow-sm ${className}`}
    >
      <option value="">Select</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/** Turn domain / vhost text into a safe https URL for opening in the browser. */
function domainToUrl(domain) {
  const raw = (domain || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const host = raw.replace(/^\/+|\/+$/g, '').split('/')[0];
  if (!host) return null;
  return `https://${host}`;
}

function DomainVHostLink({ domain }) {
  const label = (domain || '').trim();
  const url = domainToUrl(label);
  if (!label) return <span className="text-gray-400">—</span>;
  if (!url) return <span>{label}</span>;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${url} in browser`}
      className="font-medium text-indigo-600 hover:text-red-600 hover:underline cursor-pointer"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </a>
  );
}

function SectionTable({ headers, children }) {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-gray-700 font-semibold uppercase text-[10px] tracking-wider">
            {headers.map((h) => (
              <th key={h} className="px-3 py-2.5 border-r border-gray-200 last:border-r-0 uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">{children}</tbody>
      </table>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ServerChildDetails({ serverId, onParentChange, onChildChange }) {
  const [activeTab, setActiveTab] = useState('asset');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [appSearch, setAppSearch] = useState('');
  const [appPage, setAppPage] = useState(0);
  const [appPageSize, setAppPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchChildDetails(serverId);
      // Backend returns a JSON object with all child tables
      if (res.data && typeof res.data === 'object') {
        setData(res.data);
      } else {
        setError('Unexpected response format from server.');
      }
    } catch (e) {
      setError('Failed to load server configurations. Check your network connection.');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => { load(); }, [load]);

  const applications = data?.applications || [];

  const filteredApps = useMemo(() => {
    if (!appSearch.trim()) return applications;
    const lower = appSearch.toLowerCase();
    return applications.filter(a => 
      a.domain?.toLowerCase().includes(lower) || 
      a.app_name?.toLowerCase().includes(lower) ||
      a.app_path?.toLowerCase().includes(lower)
    );
  }, [applications, appSearch]);

  const paginatedApps = useMemo(() => {
    if (appPageSize === 'All') return filteredApps;
    const size = parseInt(appPageSize, 10);
    const start = appPage * size;
    return filteredApps.slice(start, start + size);
  }, [filteredApps, appPage, appPageSize]);

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Applications');
    
    worksheet.columns = [
      { header: 'Domain / VHost', key: 'domain', width: 30 },
      { header: 'App Name', key: 'app_name', width: 20 },
      { header: 'App Path', key: 'app_path', width: 40 },
      { header: 'Database Used', key: 'db_used', width: 20 },
      { header: 'SSL Expiry', key: 'ssl_expiry', width: 25 },
    ];
    
    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
    
    filteredApps.forEach(app => {
      worksheet.addRow({
        domain: app.domain || '—',
        app_name: app.app_name || 'Website',
        app_path: app.app_path || '—',
        db_used: app.db_used || '—',
        ssl_expiry: app.ssl_expiry || '—'
      });
    });
    
    const buffer = await workbook.xlsx.writeBuffer();
    const serverName = data?.asset?.server_name || 'Server';
    const dateStr = new Date().toISOString().replace(/[:.]/g, '-');
    saveAs(new Blob([buffer]), `${serverName}_Applications_${dateStr}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
        <FiRefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
        <span className="text-sm font-medium text-gray-600">Loading server configurations…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-6 bg-red-50 rounded-xl border border-red-200">
        <FiAlertCircle className="w-5 h-5 text-red-500 shrink-0" />
        <span className="text-sm text-red-700">{error}</span>
        <button
          onClick={load}
          className="ml-auto px-3 py-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
        >
          Retry
        </button>
      </div>
    );
  }

  const {
    asset = {}, storage = [], backup = {}, network = {}, location = {},
    services = [], security = {}, operations = {}, web = {}, database = {},
  } = data || {};

  const p = (field, val) => onParentChange?.(serverId, field, val);
  const c = (table, field, val) => onChildChange?.(serverId, table, field, val);

  const handleStorageChange = (index, field, val) => {
    if (!data) return;
    const newData = { ...data };
    if (!newData.storage) newData.storage = [];
    const newStorage = [...newData.storage];
    newStorage[index] = { ...newStorage[index], [field]: val };
    newData.storage = newStorage;
    setData(newData);
    c('server_storage_inventory', 'full_array', JSON.stringify(newStorage));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Tab bar */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <div className="flex gap-0 min-w-max">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-3 text-xs font-semibold border-b-2 transition whitespace-nowrap
                ${activeTab === t.key
                  ? 'border-red-700 text-red-700 bg-red-50'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-4 space-y-6">

        {/* ── Asset Info ─────────────────────────────────────────────────── */}
        {activeTab === 'asset' && (
          <SectionTable headers={[
            'Processor Model', 'CPU Physical', 'CPU Cores', 'CPU Threads',
            'Kernel Version', 'Virtual/Physical', 'Hypervisor', 'Purchase Date',
            'Warranty Expiry', 'Purpose', 'Notes',
          ]}>
            <tr>
              {[
                ['processor_model','text'], ['cpu_physical_count','text'],
                ['cpu_core_count','text'],  ['cpu_thread_count','text'],
                ['kernel_version','text'],
              ].map(([field, type]) => (
                <td key={field} className="px-3 py-2">
                  <EditInput type={type} value={asset[field]} onChange={(v) => p(field, v)} />
                </td>
              ))}
              <td className="px-3 py-2">
                <EditSelect
                  value={asset.is_virtual}
                  options={[{ value:'1', label:'Virtual' }, { value:'0', label:'Physical' }]}
                  onChange={(v) => p('is_virtual', v)}
                />
              </td>
              {[
                ['hypervisor_type','text'], ['purchase_date','date'],
                ['warranty_expiry','date'], ['purpose','text'], ['notes','text'],
              ].map(([field, type]) => (
                <td key={field} className="px-3 py-2">
                  <EditInput type={type} value={asset[field]} onChange={(v) => p(field, v)} />
                </td>
              ))}
            </tr>
          </SectionTable>
        )}

        {/* ── Storage & Backup ────────────────────────────────────────────── */}
        {activeTab === 'backup' && (
          <>
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Storage Mounts</h4>
              <SectionTable headers={['Mount Point','Filesystem','Total Size','Used','Free','Usage %']}>
                {storage.length ? storage.map((st, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2"><EditInput value={st.mount_point} onChange={(v) => handleStorageChange(i, 'mount_point', v)} /></td>
                    <td className="px-3 py-2"><EditInput value={st.filesystem_type} onChange={(v) => handleStorageChange(i, 'filesystem_type', v)} /></td>
                    <td className="px-3 py-2"><EditInput value={st.total_size} onChange={(v) => handleStorageChange(i, 'total_size', v)} /></td>
                    <td className="px-3 py-2"><EditInput value={st.used_size} onChange={(v) => handleStorageChange(i, 'used_size', v)} /></td>
                    <td className="px-3 py-2"><EditInput value={st.free_size} onChange={(v) => handleStorageChange(i, 'free_size', v)} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <EditInput value={st.usage_percent} onChange={(v) => handleStorageChange(i, 'usage_percent', v)} className="w-16" />
                        <span className={`px-2 py-0.5 rounded-full font-bold text-[10px]
                          ${(parseFloat(st.usage_percent) || 0) > 80 ? 'bg-red-100 text-red-700'
                            : (parseFloat(st.usage_percent) || 0) > 60 ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'}`}
                        >
                          {(parseFloat(st.usage_percent) || 0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                )) : <tr><td colSpan={6} className="p-4 text-center text-gray-400">No storage mounts found</td></tr>}
              </SectionTable>
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Backup Configuration</h4>
              <SectionTable headers={[
                'Local Backup Path','Local Backup Size','Retention Days','Last Backup Date',
                'Last Backup Status','DR Server Name','DR Server IP','DR Folder',
                'DR Last Sync','DR Sync Status',
              ]}>
                <tr>
                  {[
                    ['local_backup_path','text'], ['local_backup_size','text'],
                    ['backup_retention_days','text'],
                  ].map(([f,t]) => (
                    <td key={f} className="px-3 py-2">
                      <EditInput type={t} value={backup[f]} onChange={(v) => c('server_backup_config', f, v)} />
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <EditInput type="date" value={(backup.last_backup_date||'').split(' ')[0]}
                      onChange={(v) => c('server_backup_config', 'last_backup_date', v)} />
                  </td>
                  {[
                    ['last_backup_status','text'], ['dr_server_name','text'],
                    ['dr_server_ip','text'], ['dr_folder','text'],
                  ].map(([f,t]) => (
                    <td key={f} className="px-3 py-2">
                      <EditInput type={t} value={backup[f]} onChange={(v) => c('server_backup_config', f, v)} />
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <EditInput type="date" value={(backup.dr_last_sync_date||'').split(' ')[0]}
                      onChange={(v) => c('server_backup_config', 'dr_last_sync_date', v)} />
                  </td>
                  <td className="px-3 py-2">
                    <EditInput type="text" value={backup.dr_last_sync_status}
                      onChange={(v) => c('server_backup_config', 'dr_last_sync_status', v)} />
                  </td>
                </tr>
              </SectionTable>
            </div>
          </>
        )}

        {/* ── Network & IP ────────────────────────────────────────────────── */}
        {activeTab === 'network' && (
          <SectionTable headers={[
            'Private IPs','Public IPs','Failover IP','Gateway IP',
            'DNS Servers','Subnet Mask','VPN Access','Mgmt URL','Mgmt IP','SSH Port','Remote Access',
          ]}>
            <tr>
              {[
                'internal_ip','external_ip','failover_ip','gateway_ip',
                'dns','subnet_mask','vpn_access','management_url','management_ip','ssh_port','remote_access_method',
              ].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput value={network[f]} onChange={(v) => c('server_networks', f, v)} />
                </td>
              ))}
            </tr>
          </SectionTable>
        )}

        {/* ── Location ─────────────────────────────────────────────────────── */}
        {activeTab === 'location' && (
          <SectionTable headers={[
            'Location Name','Latitude','Longitude','Owner Name','Owner Contact',
            'Support Contact','Support Phone','Supervisor Name','Supervisor Phone',
            'Facility Type','Internet Provider','Power Source','Rack Location','Notes',
          ]}>
            <tr>
              {[
                'location_server','latitude','longitude','location_owner_name','location_owner_contact',
                'support_contact_name','support_contact_phone','support_supervisor_name','support_supervisor_phone',
              ].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput value={location[f]} onChange={(v) => c('server_locations', f, v)} />
                </td>
              ))}
              <td className="px-3 py-2">
                <EditSelect value={location.facility_type}
                  options={[{ value:'Owned',label:'Owned' },{ value:'Rented',label:'Rented' },{ value:'Leased',label:'Leased' }]}
                  onChange={(v) => c('server_locations', 'facility_type', v)} />
              </td>
              {['internet_provider','power_source','rack_position','notes'].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput value={location[f]} onChange={(v) => c('server_locations', f, v)} />
                </td>
              ))}
            </tr>
          </SectionTable>
        )}

        {/* ── Services ─────────────────────────────────────────────────────── */}
        {activeTab === 'services' && (
          <SectionTable headers={['Service Name','Version','Ports']}>
            {services.length ? services.map((s,i) => (
              <tr key={i}>
                <td className="px-3 py-2 font-medium">{s.service_name}</td>
                <td className="px-3 py-2">{s.service_version}</td>
                <td className="px-3 py-2 font-mono text-indigo-600">{s.ports}</td>
              </tr>
            )) : <tr><td colSpan={3} className="p-4 text-center text-gray-400">No services recorded</td></tr>}
          </SectionTable>
        )}

        {/* ── Applications ─────────────────────────────────────────────────── */}
        {activeTab === 'apps' && (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50">
              <div className="flex items-center gap-4">
                <h3 className="text-sm font-semibold text-gray-800">Deployed Applications</h3>
                {filteredApps.length > 0 && (
                    <div className="flex items-center gap-2 border-l border-gray-300 pl-3">
                      <select 
                        value={appPageSize} 
                        onChange={(e) => { setAppPageSize(e.target.value === 'All' ? 'All' : Number(e.target.value)); setAppPage(0); }}
                        className="border border-gray-200 rounded-lg px-2 py-1 outline-none focus:ring-1 focus:ring-red-500 bg-white font-medium text-gray-700"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="All">All</option>
                      </select>
                      <span className="text-gray-500 text-xs">Entries</span>
                    </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Search applications…"
                    value={appSearch}
                    onChange={(e) => { setAppSearch(e.target.value); setAppPage(0); }}
                    className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-red-500 w-full sm:w-56"
                  />
                </div>
                <button
                  onClick={handleExportExcel}
                  title="Download Excel Report"
                  className="flex items-center justify-center p-1.5 text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition"
                >
                  <FiDownload className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-gray-700 font-semibold uppercase text-[10px] tracking-wider">
                    <th className="px-3 py-2.5 border-r border-gray-100">Domain / VHost</th>
                    <th className="px-3 py-2.5 border-r border-gray-100">App Name</th>
                    <th className="px-3 py-2.5 border-r border-gray-100">App Path</th>
                    <th className="px-3 py-2.5 border-r border-gray-100">Database Used</th>
                    <th className="px-3 py-2.5">SSL Expiry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedApps.length > 0 ? (
                    paginatedApps.map((a, i) => (
                      <tr key={i} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 border-r border-gray-50">
                          <DomainVHostLink domain={a.domain} />
                        </td>
                        <td className="px-3 py-2 font-semibold border-r border-gray-50">{a.app_name || 'Website'}</td>
                        <td className="px-3 py-2 font-mono text-gray-600 border-r border-gray-50">{a.app_path || '—'}</td>
                        <td className="px-3 py-2 border-r border-gray-50">{a.db_used || '—'}</td>
                        <td className="px-3 py-2">{a.ssl_expiry || '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-gray-400">No applications match your search.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                {filteredApps.length > 0 && (
                  <span>
                    Showing {appPageSize === 'All' ? 1 : appPage * appPageSize + 1}–
                    {appPageSize === 'All' ? filteredApps.length : Math.min((appPage + 1) * appPageSize, filteredApps.length)} of {filteredApps.length} entries
                  </span>
                )}
              </div>
              {appPageSize !== 'All' && filteredApps.length > appPageSize && (() => {
                const totalPages = Math.ceil(filteredApps.length / appPageSize);
                let startPage = Math.max(0, appPage - 2);
                let endPage = Math.min(totalPages - 1, appPage + 2);
                
                if (endPage - startPage < 4) {
                  if (startPage === 0) endPage = Math.min(totalPages - 1, 4);
                  else if (endPage === totalPages - 1) startPage = Math.max(0, totalPages - 5);
                }
                
                const pages = [];
                for (let i = startPage; i <= endPage; i++) pages.push(i);

                return (
                  <div className="flex gap-1 items-center">
                    <button
                      disabled={appPage === 0}
                      onClick={() => setAppPage(p => p - 1)}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                    </button>
                    
                    {pages.map(i => (
                      <button
                        key={i}
                        onClick={() => setAppPage(i)}
                        className={`px-3 py-1.5 text-[13px] font-medium rounded-md border transition-colors ${
                          appPage === i 
                            ? 'bg-[#C61717] border-[#C61717] text-white shadow-sm' 
                            : 'bg-white border-gray-200 text-[#0a58ca] hover:bg-blue-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      disabled={appPage === totalPages - 1}
                      onClick={() => setAppPage(p => p + 1)}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── Security ─────────────────────────────────────────────────────── */}
        {activeTab === 'security' && (
          <SectionTable headers={[
            'UFW Status','Fail2ban','Crowdsec','SSH Root Login','Password Auth',
            'Last Security Patch','SSL Certs Expiring (<30d)',
          ]}>
            <tr>
              {['ufw_status','fail2ban_status','crowdsec_status'].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput value={security[f]} onChange={(v) => c('server_security_info', f, v)} />
                </td>
              ))}
              {[
                ['ssh_root_login_enabled','SSH Root Login'],
                ['password_auth_enabled','Password Auth'],
              ].map(([f, label]) => (
                <td key={f} className="px-3 py-2">
                  <EditSelect value={security[f]}
                    options={[{ value:'1', label:'Yes' }, { value:'0', label:'No' }]}
                    onChange={(v) => c('server_security_info', f, v)} />
                </td>
              ))}
              <td className="px-3 py-2">
                <EditInput type="date" value={security.last_security_patch_date}
                  onChange={(v) => c('server_security_info', 'last_security_patch_date', v)} />
              </td>
              <td className="px-3 py-2">
                <EditInput value={security.ssl_expiring_30_days}
                  onChange={(v) => c('server_security_info', 'ssl_expiring_30_days', v)} />
              </td>
            </tr>
          </SectionTable>
        )}

        {/* ── Operations ───────────────────────────────────────────────────── */}
        {activeTab === 'operations' && (
          <SectionTable headers={[
            'Reboot Schedule','Hardware Status','Uptime','Last Reboot','Current Load',
            'Memory Utilization','Disk Utilization','Open Ports','Cron Jobs','Running Services','Notes',
          ]}>
            <tr>
              {[
                'reboot_schedule','hardware_status','uptime','last_reboot','current_load',
                'memory_utilization','disk_utilization','open_ports','cron_jobs_count',
                'running_services_count','notes',
              ].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput value={operations[f]} onChange={(v) => c('server_operations_state', f, v)} />
                </td>
              ))}
            </tr>
          </SectionTable>
        )}

        {/* ── Web Server ───────────────────────────────────────────────────── */}
        {activeTab === 'web' && (
          <SectionTable headers={['Web Server Type','Version','Listener 1','Port 1','Listener 2','Port 2']}>
            <tr>
              {[
                'web_server_type','version','listener_type_1','listener_port_1',
                'listener_type_2','listener_port_2',
              ].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput value={web[f]} onChange={(v) => c('web_server_config', f, v)} />
                </td>
              ))}
            </tr>
          </SectionTable>
        )}

        {/* ── Database ─────────────────────────────────────────────────────── */}
        {activeTab === 'db' && (
          <SectionTable headers={['DB 1 Name','DB 2 Name','DB Type','DB Version','DB User','DB Password']}>
            <tr>
              {['db_1_name','db_2_name','db_type','db_version','db_username','db_password'].map((f) => (
                <td key={f} className="px-3 py-2">
                  <EditInput type={f === 'db_password' ? 'password' : 'text'}
                    value={database[f]} onChange={(v) => c('database_servers', f, v)} />
                </td>
              ))}
            </tr>
          </SectionTable>
        )}
      </div>
    </div>
  );
}
